import React from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import { addSensorSystem } from "../../../../store/features/optionalSystemsSlice";
import { SensorSystem } from "../../../../types/optionalSystems";
import {
  validateDeepPenetrationScanners,
  validateExtensionNet,
  validateDistributedArray,
  validateExtendedArray,
  validateRapidDeploymentExtendedArray,
} from "../../../../utils/calculations/optionalSystems";
import { formatCurrency, formatNumber } from "../../../../utils/formatting";
import { OPTIONAL_SYSTEM_TYPES } from "../../../../constants/optionalSystems";
import { selectors as shipBuilderSelectors } from "../../../../store/selectors/shipBuilderSelectors";
import { selectors } from "../../../../store/selectors";

const COUNTERMEASURE_IDS = [
  "countermeasuresSuite",
  "militaryCountermeasuresSuite",
];
const LIFE_SCANNER_IDS = ["lifeScanner", "lifeScannerAnalysisSuite"];
const MAIL_DISTRIBUTION_IDS = [
  "mailDistributionArray10",
  "mailDistributionArray13",
];
const SIGNAL_PROCESSING_IDS = [
  "improvedSignalProcessing",
  "enhancedSignalProcessing",
];

interface SensorSystemsProps {
  availableSystems: any[];
  shipTonnage: number;
  techLevel: number;
  tlOverrideActive?: boolean;
}

const SensorSystems: React.FC<SensorSystemsProps> = ({
  availableSystems,
  shipTonnage,
  techLevel,
  tlOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();
  const systems = useAppSelector((state) => state.optionalSystems.sensors);
  const [customTons, setCustomTons] = React.useState<{ [id: string]: number }>(
    {},
  );
  const baseSuite = useAppSelector(shipBuilderSelectors.selectSensors);
  const [selectedCountermeasure, setSelectedCountermeasure] = React.useState(
    COUNTERMEASURE_IDS[0],
  );
  const [selectedLifeScanner, setSelectedLifeScanner] = React.useState(
    LIFE_SCANNER_IDS[0],
  );
  const [selectedMailDistribution, setSelectedMailDistribution] =
    React.useState(MAIL_DISTRIBUTION_IDS[0]);
  const [selectedSignalProcessing, setSelectedSignalProcessing] =
    React.useState(SIGNAL_PROCESSING_IDS[0]);

  // Filter out grouped systems from the main list
  const SENSOR_SYSTEMS = OPTIONAL_SYSTEM_TYPES.filter(
    (s) =>
      s.type === "sensor" &&
      !COUNTERMEASURE_IDS.includes(s.id) &&
      !LIFE_SCANNER_IDS.includes(s.id) &&
      !MAIL_DISTRIBUTION_IDS.includes(s.id) &&
      !SIGNAL_PROCESSING_IDS.includes(s.id),
  );
  const countermeasureOptions = OPTIONAL_SYSTEM_TYPES.filter((s) =>
    COUNTERMEASURE_IDS.includes(s.id),
  );
  const selectedCountermeasureObj = countermeasureOptions.find(
    (s) => s.id === selectedCountermeasure,
  )!;
  const lifeScannerOptions = OPTIONAL_SYSTEM_TYPES.filter((s) =>
    LIFE_SCANNER_IDS.includes(s.id),
  );
  const selectedLifeScannerObj = lifeScannerOptions.find(
    (s) => s.id === selectedLifeScanner,
  )!;
  const mailDistributionOptions = OPTIONAL_SYSTEM_TYPES.filter((s) =>
    MAIL_DISTRIBUTION_IDS.includes(s.id),
  );
  const selectedMailDistributionObj = mailDistributionOptions.find(
    (s) => s.id === selectedMailDistribution,
  )!;
  const signalProcessingOptions = OPTIONAL_SYSTEM_TYPES.filter((s) =>
    SIGNAL_PROCESSING_IDS.includes(s.id),
  );
  const selectedSignalProcessingObj = signalProcessingOptions.find(
    (s) => s.id === selectedSignalProcessing,
  )!;

  // Precompute selector results for all sensor system types at the top level
  const deepPenetrationScannersSpecs = useAppSelector((state) =>
    selectors.derived.selectDeepPenetrationScannersSpecs(
      state,
      customTons["deepPenetrationScanners"] || 1,
    ),
  );
  const extensionNetSpecs = useAppSelector(
    selectors.derived.selectExtensionNetSpecs,
  );

  // Precompute array specs if baseSuite exists
  const baseSuiteForCalc = baseSuite
    ? {
        tonnage: baseSuite.tons,
        cost: baseSuite.cost,
        power: baseSuite.power,
      }
    : null;

  const distributedArraySpecs = useAppSelector((state) =>
    selectors.derived.selectDistributedArraySpecs(
      state,
      baseSuiteForCalc ?? { tonnage: 0, cost: 0, power: 0 },
    ),
  );
  const extendedArraySpecs = useAppSelector((state) =>
    selectors.derived.selectExtendedArraySpecs(
      state,
      baseSuiteForCalc ?? { tonnage: 0, cost: 0, power: 0 },
    ),
  );
  const rapidDeploymentExtendedArraySpecs = useAppSelector((state) =>
    selectors.derived.selectRapidDeploymentExtendedArraySpecs(
      state,
      baseSuiteForCalc ?? { tonnage: 0, cost: 0, power: 0 },
    ),
  );

  // Example usage for deep penetration scanners:
  // const deepPenetrationSpecs = useAppSelector(state => selectors.derived.selectDeepPenetrationScannersSpecs(state, customTons['deepPenetrationScanners'] || 1));
  // For extension net:
  // const extensionNetSpecs = useAppSelector(selectors.derived.selectExtensionNetSpecs);
  // For distributed/extended/rapid arrays, pass baseSuite as needed.

  // Refactor all calculation logic in the UI to use these selectors instead of direct calls.

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Sensor Systems</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Available Sensor Systems
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Countermeasures Card with Dropdown */}
          <div className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 dark:bg-gray-700 border-gray-200 dark:border-gray-600">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Countermeasures Suite
              </div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
                value={selectedCountermeasure}
                onChange={(e) => setSelectedCountermeasure(e.target.value)}
              >
                {countermeasureOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div>
                  <b>Cost:</b> {formatCurrency(selectedCountermeasureObj.cost)}
                </div>
                <div>
                  <b>Tonnage:</b>{" "}
                  {formatNumber(selectedCountermeasureObj.tonnage)} tons
                </div>
                <div>
                  <b>Power:</b> {formatNumber(selectedCountermeasureObj.power)}
                </div>
              </div>
            </div>
            <button
              className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                const system: SensorSystem = {
                  id: `${selectedCountermeasureObj.id}_${systems.length + 1}`,
                  type: "sensor",
                  subtype:
                    selectedCountermeasureObj.subtype as SensorSystem["subtype"],
                  name: selectedCountermeasureObj.name,
                  description: selectedCountermeasureObj.description,
                  cost: selectedCountermeasureObj.cost,
                  tonnage: selectedCountermeasureObj.tonnage,
                  power: selectedCountermeasureObj.power,
                  techLevel: selectedCountermeasureObj.techLevel,
                  properties: { ...selectedCountermeasureObj.properties },
                };
                dispatch(addSensorSystem(system));
              }}
            >
              Add
            </button>
          </div>
          {/* Life Scanner Card with Dropdown */}
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Life Scanner
              </div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
                value={selectedLifeScanner}
                onChange={(e) => setSelectedLifeScanner(e.target.value)}
              >
                {lifeScannerOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div>
                  <b>Cost:</b> {formatCurrency(selectedLifeScannerObj.cost)}
                </div>
                <div>
                  <b>Tonnage:</b> {formatNumber(selectedLifeScannerObj.tonnage)}{" "}
                  tons
                </div>
                <div>
                  <b>Power:</b> {formatNumber(selectedLifeScannerObj.power)}
                </div>
              </div>
            </div>
            <button
              className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                const system: SensorSystem = {
                  id: `${selectedLifeScannerObj.id}_${systems.length + 1}`,
                  type: "sensor",
                  subtype:
                    selectedLifeScannerObj.subtype as SensorSystem["subtype"],
                  name: selectedLifeScannerObj.name,
                  description: selectedLifeScannerObj.description,
                  cost: selectedLifeScannerObj.cost,
                  tonnage: selectedLifeScannerObj.tonnage,
                  power: selectedLifeScannerObj.power,
                  techLevel: selectedLifeScannerObj.techLevel,
                  properties: { ...selectedLifeScannerObj.properties },
                };
                dispatch(addSensorSystem(system));
              }}
            >
              Add
            </button>
          </div>
          {/* Mail Distribution Array Card with Dropdown */}
          <div className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Mail Distribution Array
              </div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-600 dark:text-gray-100 text-sm mb-2"
                value={selectedMailDistribution}
                onChange={(e) => setSelectedMailDistribution(e.target.value)}
              >
                {mailDistributionOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div>
                  <b>Cost:</b>{" "}
                  {formatCurrency(selectedMailDistributionObj.cost)}
                </div>
                <div>
                  <b>Tonnage:</b>{" "}
                  {formatNumber(selectedMailDistributionObj.tonnage)} tons
                </div>
                <div>
                  <b>Power:</b>{" "}
                  {formatNumber(selectedMailDistributionObj.power)}
                </div>
              </div>
            </div>
            <button
              className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                const system: SensorSystem = {
                  id: `${selectedMailDistributionObj.id}_${systems.length + 1}`,
                  type: "sensor",
                  subtype:
                    selectedMailDistributionObj.subtype as SensorSystem["subtype"],
                  name: selectedMailDistributionObj.name,
                  description: selectedMailDistributionObj.description,
                  cost: selectedMailDistributionObj.cost,
                  tonnage: selectedMailDistributionObj.tonnage,
                  power: selectedMailDistributionObj.power,
                  techLevel: selectedMailDistributionObj.techLevel,
                  properties: { ...selectedMailDistributionObj.properties },
                };
                dispatch(addSensorSystem(system));
              }}
            >
              Add
            </button>
          </div>
          {/* Signal Processing Card with Dropdown */}
          <div className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Signal Processing
              </div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-600 dark:text-gray-100 text-sm mb-2"
                value={selectedSignalProcessing}
                onChange={(e) => setSelectedSignalProcessing(e.target.value)}
              >
                {signalProcessingOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div>
                  <b>Cost:</b>{" "}
                  {formatCurrency(selectedSignalProcessingObj.cost)}
                </div>
                <div>
                  <b>Tonnage:</b>{" "}
                  {formatNumber(selectedSignalProcessingObj.tonnage)} tons
                </div>
                <div>
                  <b>Power:</b>{" "}
                  {formatNumber(selectedSignalProcessingObj.power)}
                </div>
              </div>
            </div>
            <button
              className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                const system: SensorSystem = {
                  id: `${selectedSignalProcessingObj.id}_${systems.length + 1}`,
                  type: "sensor",
                  subtype:
                    selectedSignalProcessingObj.subtype as SensorSystem["subtype"],
                  name: selectedSignalProcessingObj.name,
                  description: selectedSignalProcessingObj.description,
                  cost: selectedSignalProcessingObj.cost,
                  tonnage: selectedSignalProcessingObj.tonnage,
                  power: selectedSignalProcessingObj.power,
                  techLevel: selectedSignalProcessingObj.techLevel,
                  properties: { ...selectedSignalProcessingObj.properties },
                };
                dispatch(addSensorSystem(system));
              }}
            >
              Add
            </button>
          </div>
          {/* Extended Array Card */}
          {SENSOR_SYSTEMS.filter((s) => s.id === "extendedArray").map(
            (selected) => {
              let errors: string[] = [];
              let note: string | undefined = undefined;
              let tonsValue = customTons[selected.id] || 1;
              let calcResult = selected;
              if (selected.subtype === "deepPenetrationScanners") {
                const specs = deepPenetrationScannersSpecs as any;
                calcResult = {
                  ...selected,
                  cost: specs.cost,
                  tonnage: specs.tonnage,
                  power: specs.power,
                  techLevel: specs.techLevel,
                };
                note = specs.note;
                errors = validateDeepPenetrationScanners(tonsValue);
              } else if (selected.subtype === "extensionNet") {
                const specs = extensionNetSpecs as any;
                calcResult = {
                  ...selected,
                  cost: specs.cost,
                  tonnage: specs.tonnage,
                  power: specs.power,
                  techLevel: specs.techLevel,
                };
                note = specs.note;
                errors = validateExtensionNet(shipTonnage);
              } else if (
                [
                  "distributedArray",
                  "extendedArray",
                  "rapidDeploymentExtendedArray",
                ].includes(selected.subtype)
              ) {
                if (!baseSuite) {
                  errors = [
                    "You must select a base sensor suite in Step 7 before adding this system.",
                  ];
                } else {
                  if (selected.subtype === "distributedArray") {
                    const specs = distributedArraySpecs as any;
                    calcResult = {
                      ...selected,
                      cost: specs.cost,
                      tonnage: specs.tonnage,
                      power: specs.power,
                      techLevel: specs.techLevel,
                    };
                    note = specs.note;
                    errors = validateDistributedArray(
                      baseSuiteForCalc!,
                      shipTonnage,
                      ["IMPROVED", "ADVANCED"].includes(baseSuite.type),
                    );
                  } else if (selected.subtype === "extendedArray") {
                    const specs = extendedArraySpecs as any;
                    calcResult = {
                      ...selected,
                      cost: specs.cost,
                      tonnage: specs.tonnage,
                      power: specs.power,
                      techLevel: specs.techLevel,
                    };
                    note = specs.note;
                    errors = validateExtendedArray(baseSuiteForCalc!);
                  } else if (
                    selected.subtype === "rapidDeploymentExtendedArray"
                  ) {
                    const specs = rapidDeploymentExtendedArraySpecs as any;
                    calcResult = {
                      ...selected,
                      cost: specs.cost,
                      tonnage: specs.tonnage,
                      power: specs.power,
                      techLevel: specs.techLevel,
                    };
                    note = specs.note;
                    errors = validateRapidDeploymentExtendedArray(
                      baseSuiteForCalc!,
                    );
                  }
                }
              }

              const handleAdd = () => {
                if (errors.length) return;
                const system: SensorSystem = {
                  id: `${selected.id}_${systems.length + 1}`,
                  type: "sensor",
                  subtype: selected.subtype as SensorSystem["subtype"],
                  name: selected.name,
                  description: selected.description,
                  cost: calcResult.cost,
                  tonnage: calcResult.tonnage,
                  power: calcResult.power,
                  techLevel: calcResult.techLevel,
                  properties: { ...selected.properties },
                };
                dispatch(addSensorSystem(system));
              };

              return (
                <div
                  key={selected.id}
                  className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700"
                >
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      {selected.name}
                    </div>
                    {selected.subtype === "deepPenetrationScanners" && (
                      <div className="mb-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Tonnage
                        </label>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={tonsValue}
                          onChange={(e) =>
                            setCustomTons({
                              ...customTons,
                              [selected.id]: Math.max(
                                1,
                                Math.floor(Number(e.target.value)),
                              ),
                            })
                          }
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-600 dark:text-gray-100 text-sm"
                        />
                      </div>
                    )}
                    <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                      <div>
                        <b>Cost:</b> {formatCurrency(calcResult.cost)}
                      </div>
                      <div>
                        <b>Tonnage:</b> {formatNumber(calcResult.tonnage)} tons
                      </div>
                      <div>
                        <b>Power:</b> {formatNumber(calcResult.power)}
                      </div>
                    </div>
                    {note && (
                      <div className="text-xs text-blue-700 mb-2">
                        {note.replace(/([0-9]+\.[0-9]+)/g, (m) =>
                          formatNumber(Number(m)),
                        )}
                      </div>
                    )}
                    {errors.length > 0 && (
                      <div className="text-xs text-red-600 mb-2">
                        {errors.map((e) => (
                          <div key={e}>{e}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    className={`px-4 py-1 rounded text-white font-semibold ${errors.length > 0 ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                    onClick={handleAdd}
                    disabled={errors.length > 0}
                  >
                    Add
                  </button>
                  {[
                    "distributedArray",
                    "extendedArray",
                    "rapidDeploymentExtendedArray",
                  ].includes(selected.subtype) &&
                    baseSuite && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                        <div className="font-semibold">Base Sensor Suite:</div>
                        <div>Type: {baseSuite.name}</div>
                        <div>Cost: {formatCurrency(baseSuite.cost)}</div>
                        <div>Tonnage: {formatNumber(baseSuite.tons)} tons</div>
                        <div>Power: {formatNumber(baseSuite.power)}</div>
                      </div>
                    )}
                </div>
              );
            },
          )}
          {/* Rapid-Deployment Extended Array Card */}
          {SENSOR_SYSTEMS.filter(
            (s) => s.id === "rapidDeploymentExtendedArray",
          ).map((selected) => {
            let errors: string[] = [];
            let note: string | undefined = undefined;
            let tonsValue = customTons[selected.id] || 1;
            let calcResult = selected;
            if (selected.subtype === "deepPenetrationScanners") {
              const specs = deepPenetrationScannersSpecs as any;
              calcResult = {
                ...selected,
                cost: specs.cost,
                tonnage: specs.tonnage,
                power: specs.power,
                techLevel: specs.techLevel,
              };
              note = specs.note;
              errors = validateDeepPenetrationScanners(tonsValue);
            } else if (selected.subtype === "extensionNet") {
              const specs = extensionNetSpecs as any;
              calcResult = {
                ...selected,
                cost: specs.cost,
                tonnage: specs.tonnage,
                power: specs.power,
                techLevel: specs.techLevel,
              };
              note = specs.note;
              errors = validateExtensionNet(shipTonnage);
            } else if (
              [
                "distributedArray",
                "extendedArray",
                "rapidDeploymentExtendedArray",
              ].includes(selected.subtype)
            ) {
              if (!baseSuite) {
                errors = [
                  "You must select a base sensor suite in Step 7 before adding this system.",
                ];
              } else {
                if (selected.subtype === "distributedArray") {
                  const specs = distributedArraySpecs as any;
                  calcResult = {
                    ...selected,
                    cost: specs.cost,
                    tonnage: specs.tonnage,
                    power: specs.power,
                    techLevel: specs.techLevel,
                  };
                  note = specs.note;
                  errors = validateDistributedArray(
                    baseSuiteForCalc!,
                    shipTonnage,
                    ["IMPROVED", "ADVANCED"].includes(baseSuite.type),
                  );
                } else if (selected.subtype === "extendedArray") {
                  const specs = extendedArraySpecs as any;
                  calcResult = {
                    ...selected,
                    cost: specs.cost,
                    tonnage: specs.tonnage,
                    power: specs.power,
                    techLevel: specs.techLevel,
                  };
                  note = specs.note;
                  errors = validateExtendedArray(baseSuiteForCalc!);
                } else if (
                  selected.subtype === "rapidDeploymentExtendedArray"
                ) {
                  const specs = rapidDeploymentExtendedArraySpecs as any;
                  calcResult = {
                    ...selected,
                    cost: specs.cost,
                    tonnage: specs.tonnage,
                    power: specs.power,
                    techLevel: specs.techLevel,
                  };
                  note = specs.note;
                  errors = validateRapidDeploymentExtendedArray(
                    baseSuiteForCalc!,
                  );
                }
              }
            }

            const handleAdd = () => {
              if (errors.length) return;
              const system: SensorSystem = {
                id: `${selected.id}_${systems.length + 1}`,
                type: "sensor",
                subtype: selected.subtype as SensorSystem["subtype"],
                name: selected.name,
                description: selected.description,
                cost: calcResult.cost,
                tonnage: calcResult.tonnage,
                power: calcResult.power,
                techLevel: calcResult.techLevel,
                properties: { ...selected.properties },
              };
              dispatch(addSensorSystem(system));
            };

            return (
              <div
                key={selected.id}
                className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700"
              >
                <div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {selected.name}
                  </div>
                  {selected.subtype === "deepPenetrationScanners" && (
                    <div className="mb-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Tonnage
                      </label>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={tonsValue}
                        onChange={(e) =>
                          setCustomTons({
                            ...customTons,
                            [selected.id]: Math.max(
                              1,
                              Math.floor(Number(e.target.value)),
                            ),
                          })
                        }
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-600 dark:text-gray-100 text-sm"
                      />
                    </div>
                  )}
                  <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                    <div>
                      <b>Cost:</b> {formatCurrency(calcResult.cost)}
                    </div>
                    <div>
                      <b>Tonnage:</b> {formatNumber(calcResult.tonnage)} tons
                    </div>
                    <div>
                      <b>Power:</b> {formatNumber(calcResult.power)}
                    </div>
                  </div>
                  {note && (
                    <div className="text-xs text-blue-700 mb-2">
                      {note.replace(/([0-9]+\.[0-9]+)/g, (m) =>
                        formatNumber(Number(m)),
                      )}
                    </div>
                  )}
                  {errors.length > 0 && (
                    <div className="text-xs text-red-600 mb-2">
                      {errors.map((e) => (
                        <div key={e}>{e}</div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className={`px-4 py-1 rounded text-white font-semibold ${errors.length > 0 ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                  onClick={handleAdd}
                  disabled={errors.length > 0}
                >
                  Add
                </button>
                {[
                  "distributedArray",
                  "extendedArray",
                  "rapidDeploymentExtendedArray",
                ].includes(selected.subtype) &&
                  baseSuite && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                      <div className="font-semibold">Base Sensor Suite:</div>
                      <div>Type: {baseSuite.name}</div>
                      <div>Cost: {formatCurrency(baseSuite.cost)}</div>
                      <div>Tonnage: {formatNumber(baseSuite.tons)} tons</div>
                      <div>Power: {formatNumber(baseSuite.power)}</div>
                    </div>
                  )}
              </div>
            );
          })}
          {/* All other sensor systems as cards */}
          {SENSOR_SYSTEMS.filter(
            (s) =>
              s.id !== "extendedArray" &&
              s.id !== "rapidDeploymentExtendedArray",
          ).map((selected) => {
            let errors: string[] = [];
            let note: string | undefined = undefined;
            let tonsValue = customTons[selected.id] || 1;
            let calcResult = selected;
            if (selected.subtype === "deepPenetrationScanners") {
              const specs = deepPenetrationScannersSpecs as any;
              calcResult = {
                ...selected,
                cost: specs.cost,
                tonnage: specs.tonnage,
                power: specs.power,
                techLevel: specs.techLevel,
              };
              note = specs.note;
              errors = validateDeepPenetrationScanners(tonsValue);
            } else if (selected.subtype === "extensionNet") {
              const specs = extensionNetSpecs as any;
              calcResult = {
                ...selected,
                cost: specs.cost,
                tonnage: specs.tonnage,
                power: specs.power,
                techLevel: specs.techLevel,
              };
              note = specs.note;
              errors = validateExtensionNet(shipTonnage);
            } else if (
              [
                "distributedArray",
                "extendedArray",
                "rapidDeploymentExtendedArray",
              ].includes(selected.subtype)
            ) {
              if (!baseSuite) {
                errors = [
                  "You must select a base sensor suite in Step 7 before adding this system.",
                ];
              } else {
                if (selected.subtype === "distributedArray") {
                  const specs = distributedArraySpecs as any;
                  calcResult = {
                    ...selected,
                    cost: specs.cost,
                    tonnage: specs.tonnage,
                    power: specs.power,
                    techLevel: specs.techLevel,
                  };
                  note = specs.note;
                  errors = validateDistributedArray(
                    baseSuiteForCalc!,
                    shipTonnage,
                    ["IMPROVED", "ADVANCED"].includes(baseSuite.type),
                  );
                } else if (selected.subtype === "extendedArray") {
                  const specs = extendedArraySpecs as any;
                  calcResult = {
                    ...selected,
                    cost: specs.cost,
                    tonnage: specs.tonnage,
                    power: specs.power,
                    techLevel: specs.techLevel,
                  };
                  note = specs.note;
                  errors = validateExtendedArray(baseSuiteForCalc!);
                } else if (
                  selected.subtype === "rapidDeploymentExtendedArray"
                ) {
                  const specs = rapidDeploymentExtendedArraySpecs as any;
                  calcResult = {
                    ...selected,
                    cost: specs.cost,
                    tonnage: specs.tonnage,
                    power: specs.power,
                    techLevel: specs.techLevel,
                  };
                  note = specs.note;
                  errors = validateRapidDeploymentExtendedArray(
                    baseSuiteForCalc!,
                  );
                }
              }
            }

            const handleAdd = () => {
              if (errors.length) return;
              const system: SensorSystem = {
                id: `${selected.id}_${systems.length + 1}`,
                type: "sensor",
                subtype: selected.subtype as SensorSystem["subtype"],
                name: selected.name,
                description: selected.description,
                cost: calcResult.cost,
                tonnage: calcResult.tonnage,
                power: calcResult.power,
                techLevel: calcResult.techLevel,
                properties: { ...selected.properties },
              };
              dispatch(addSensorSystem(system));
            };

            return (
              <div
                key={selected.id}
                className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700"
              >
                <div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {selected.name}
                  </div>
                  {selected.subtype === "deepPenetrationScanners" && (
                    <div className="mb-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Tonnage
                      </label>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={tonsValue}
                        onChange={(e) =>
                          setCustomTons({
                            ...customTons,
                            [selected.id]: Math.max(
                              1,
                              Math.floor(Number(e.target.value)),
                            ),
                          })
                        }
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-600 dark:text-gray-100 text-sm"
                      />
                    </div>
                  )}
                  <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                    <div>
                      <b>Cost:</b> {formatCurrency(calcResult.cost)}
                    </div>
                    <div>
                      <b>Tonnage:</b> {formatNumber(calcResult.tonnage)} tons
                    </div>
                    <div>
                      <b>Power:</b> {formatNumber(calcResult.power)}
                    </div>
                  </div>
                  {note && (
                    <div className="text-xs text-blue-700 mb-2">
                      {note.replace(/([0-9]+\.[0-9]+)/g, (m) =>
                        formatNumber(Number(m)),
                      )}
                    </div>
                  )}
                  {errors.length > 0 && (
                    <div className="text-xs text-red-600 mb-2">
                      {errors.map((e) => (
                        <div key={e}>{e}</div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className={`px-4 py-1 rounded text-white font-semibold ${errors.length > 0 ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                  onClick={handleAdd}
                  disabled={errors.length > 0}
                >
                  Add
                </button>
                {[
                  "distributedArray",
                  "extendedArray",
                  "rapidDeploymentExtendedArray",
                ].includes(selected.subtype) &&
                  baseSuite && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                      <div className="font-semibold">Base Sensor Suite:</div>
                      <div>Type: {baseSuite.name}</div>
                      <div>Cost: {formatCurrency(baseSuite.cost)}</div>
                      <div>Tonnage: {formatNumber(baseSuite.tons)} tons</div>
                      <div>Power: {formatNumber(baseSuite.power)}</div>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Installed Sensor Systems section removed */}
    </div>
  );
};

export default SensorSystems;
